import { CheckCircle2, ChevronRight, Upload } from "lucide-react";
import { MonitoringProvider, MONITORING_PROVIDERS } from "../lib/api";
import { useEffect, useState } from "react";
import { SignatureBg } from "../components/SignatureBg";
import { useParams } from "react-router-dom";
import { intake, type IntakeContext } from "../lib/api";

// Funding intake wizard. Same structure as the dispute manager's intake —
// multi-step card, autosave per step — with the funding questions replacing
// the CROA agreements step. NO contract is signed here: staff analyze the
// credit report first, then send the right percentage contract to e-sign.
type Step = "identity" | "business" | "documents" | "monitoring" | "review";

const STEP_ORDER: Step[] = ["identity", "business", "documents", "monitoring", "review"];

type DocKind = "government_id" | "sos_document" | "ein_letter";

// v1.1 port: required docs gate the Continue button and name themselves in
// the error; a failed upload blocks with a retry prompt.
const DOCS: Array<{ key: DocKind; label: string; hint: string; required: boolean; accept: string }> = [
  {
    key: "government_id",
    label: "Government-issued ID (front)",
    hint: "Photo or PDF. Required.",
    required: true,
    accept: "image/*,application/pdf",
  },
  {
    key: "sos_document",
    label: "Secretary of State document (LLC / corporation filing)",
    hint: "PDF preferred — the filing you downloaded from your state. Optional.",
    required: false,
    accept: "application/pdf,image/*",
  },
  {
    key: "ein_letter",
    label: "EIN document (IRS CP-575 letter)",
    hint: "PDF preferred — the letter the IRS issued with your EIN. Optional.",
    required: false,
    accept: "application/pdf,image/*",
  },
];

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

// "$48,000" → 48000. Money/count inputs accept whatever the client types.
const toNum = (v: string) => Number(v.replace(/[^0-9.]/g, ""));

export default function IntakePage() {
  const { token = "" } = useParams();
  const [ctx, setCtx] = useState<IntakeContext | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [step, setStep] = useState<Step>("identity");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // identity
  const [legalName, setLegalName] = useState("");
  const [dob, setDob] = useState("");
  const [ssn, setSsn] = useState("");
  const [addr1, setAddr1] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [phone, setPhone] = useState("");
  const [annualIncome, setAnnualIncome] = useState("");
  const [incomeSource, setIncomeSource] = useState("");
  const [employerName, setEmployerName] = useState("");
  const [employerAddress, setEmployerAddress] = useState("");
  const [employerPhone, setEmployerPhone] = useState("");
  const [empYears, setEmpYears] = useState("");
  const [empMonths, setEmpMonths] = useState("");
  const [otherIncome, setOtherIncome] = useState<Array<{ source: string; amount: string; frequency: "monthly" | "annual" }>>([]);
  const [checkingBal, setCheckingBal] = useState("");
  const [savingsBal, setSavingsBal] = useState("");
  const [housingStatus, setHousingStatus] = useState("");
  const [housingPayment, setHousingPayment] = useState("");

  // business / funding questions
  const [capital, setCapital] = useState("");
  const [useOfFunds, setUseOfFunds] = useState("");
  const [fundingPref, setFundingPref] = useState<"personal" | "business" | "both">("both");
  const [bizName, setBizName] = useState("");
  const [bizDescription, setBizDescription] = useState("");
  const [bizEntity, setBizEntity] = useState("");
  const [bizEin, setBizEin] = useState("");
  const [bizIndustry, setBizIndustry] = useState("");
  const [bizCity, setBizCity] = useState("");
  const [bizState, setBizState] = useState("");
  const [bizZip, setBizZip] = useState("");
  const [bizStart, setBizStart] = useState("");
  const [annualRev, setAnnualRev] = useState("");

  // docs
  const [docFiles, setDocFiles] = useState<Partial<Record<DocKind, File>>>({});
  const [docStatus, setDocStatus] = useState<Record<string, "idle" | "uploading" | "done" | "error">>({});

  // monitoring
  const [monProvider, setMonProvider] = useState<Exclude<MonitoringProvider, "not_started">>("my_free_score_now");
  const [hasMonitoring, setHasMonitoring] = useState<"yes" | "no" | null>(null);
  const [ckEmail, setCkEmail] = useState("");
  const [ckPass, setCkPass] = useState("");

  // FCRA credit authorization — signed on the review step. Never autosaved:
  // a signature has to be something the client typed in this session.
  const [authAgree, setAuthAgree] = useState(false);
  const [authName, setAuthName] = useState("");
  const [noCreditKarma, setNoCreditKarma] = useState(false);
  const [monUser, setMonUser] = useState("");
  const [monPass, setMonPass] = useState("");

  const [error, setError] = useState<string | null>(null);
  const [resumed, setResumed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const c = await intake.get(token);
        setCtx(c);
        // A prospect who picked a monitoring provider on the sign-up link
        // (/start/:slug) arrives with it seeded in the draft — start there.
        const seeded = (c as any)?.draft?.monitoring_provider;
        if (seeded === "smart_credit" || seeded === "my_free_score_now") setMonProvider(seeded as any);
        setLegalName((c as any).draft?.identity?.legal_name || c.client.legal_name || "");

        // Prefill everything non-secret from the saved draft so a returning
        // client only re-enters SSN and passwords.
        const d: any = (c as any).draft || {};
        if (d.identity) {
          if (d.identity.dob) setDob(d.identity.dob);
          if (d.identity.address_line1) setAddr1(d.identity.address_line1);
          if (d.identity.address_city) setCity(d.identity.address_city);
          if (d.identity.address_state) setState(d.identity.address_state);
          if (d.identity.address_zip) setZip(d.identity.address_zip);
          if (d.identity.phone) setPhone(d.identity.phone);
        }
        if (d.funding) {
          if (d.funding.capital_requested) setCapital(String(d.funding.capital_requested));
          if (d.funding.use_of_funds) setUseOfFunds(d.funding.use_of_funds);
          if (d.funding.business_description) setBizDescription(d.funding.business_description);
          if (d.funding.business_state) setBizState(d.funding.business_state);
          if (d.funding.funding_preference) setFundingPref(d.funding.funding_preference);
          if (d.funding.annual_income != null) setAnnualIncome(String(d.funding.annual_income));
          if (d.funding.income_source) setIncomeSource(d.funding.income_source);
          if (d.funding.employer_name) setEmployerName(d.funding.employer_name);
          if (d.funding.employer_address) setEmployerAddress(d.funding.employer_address);
          if (d.funding.employer_phone) setEmployerPhone(d.funding.employer_phone);
          if (d.funding.employment_years != null) setEmpYears(String(d.funding.employment_years));
          if (d.funding.employment_months != null) setEmpMonths(String(d.funding.employment_months));
          if (Array.isArray(d.funding.other_income)) setOtherIncome(d.funding.other_income.map((o: any) => ({
            source: String(o?.source ?? ""), amount: o?.amount != null ? String(o.amount) : "",
            frequency: o?.frequency === "annual" ? "annual" : "monthly",
          })));
          if (d.funding.checking_balance != null) setCheckingBal(String(d.funding.checking_balance));
          if (d.funding.savings_balance != null) setSavingsBal(String(d.funding.savings_balance));
          if (d.funding.housing_status) setHousingStatus(d.funding.housing_status);
          if (d.funding.housing_payment != null) setHousingPayment(String(d.funding.housing_payment));
          if (d.funding.business_name) setBizName(d.funding.business_name);
          if (d.funding.business_entity_type) setBizEntity(d.funding.business_entity_type);
          if (d.funding.business_ein) setBizEin(d.funding.business_ein);
          if (d.funding.business_industry) setBizIndustry(d.funding.business_industry);
          if (d.funding.business_city) setBizCity(d.funding.business_city);
          if (d.funding.business_zip) setBizZip(d.funding.business_zip);
          if (d.funding.business_start_date) setBizStart(d.funding.business_start_date);
          if (d.funding.annual_revenue != null) setAnnualRev(String(d.funding.annual_revenue));
        }
        if (d.monitoring_provider) setMonProvider(d.monitoring_provider);
        // Documents already uploaded in an earlier session count as done.
        const restored: string[] = (c as any).restored_documents || [];
        if (restored.length) {
          setDocStatus((prev) => {
            const next = { ...prev };
            for (const k of restored) (next as any)[k] = "done";
            return next;
          });
        }

        // jump to first incomplete step (secrets only count in THIS session)
        const s = c.steps_complete;
        if (!s.identity) setStep("identity");
        else if (!s.funding) setStep("business");
        else if (!s.documents) setStep("documents");
        else if (!s.monitoring) setStep("monitoring");
        else setStep("review");

        // Coming back in a fresh session with a saved draft → explain why
        // some fields need re-entering.
        if (!s.identity && (d.identity || d.funding)) setResumed(true);
      } catch (e: any) {
        setLoadError(e?.message || "This link is invalid or expired.");
      }
    })();
  }, [token]);

  const stepIndex = STEP_ORDER.indexOf(step);

  async function saveIdentity() {
    // M-07: the UI marks these required — block Continue until they're filled
    // (previously only phone was enforced, so "required" fields could be skipped).
    const missing: string[] = [];
    if (!legalName.trim()) missing.push("your legal name");
    if (!dob) missing.push("your date of birth");
    if (!ssn.trim()) missing.push("your Social Security number");
    if (!addr1.trim()) missing.push("your street address");
    if (!city.trim()) missing.push("your city");
    if (!state.trim()) missing.push("your state");
    if (!zip.trim()) missing.push("your ZIP code");
    if (missing.length > 0) {
      setError(`Please fill in ${missing.join(", ")} before continuing.`);
      return;
    }
    if (ssn.replace(/\D/g, "").length !== 9) {
      setError("Your Social Security number should be 9 digits (XXX-XX-XXXX).");
      return;
    }
    if (phone.replace(/\D/g, "").length < 10) {
      setError("Add a phone number — every lender application asks for one.");
      return;
    }
    setBusy(true); setError(null);
    try {
      await intake.saveIdentity(token, {
        legal_name: legalName,
        dob,
        ssn_full: ssn,
        address_line1: addr1,
        address_city: city,
        address_state: state,
        address_zip: zip,
        phone,
      });
      setStep("business");
    } catch (e: any) { setError(e?.message || "Save failed"); }
    finally { setBusy(false); }
  }

  async function saveBusiness() {
    const cap = Number(capital.replace(/[^0-9.]/g, ""));
    if (!Number.isFinite(cap) || cap <= 0) { setError("Tell us how much capital you're looking to get."); return; }
    if (!useOfFunds.trim()) { setError("Tell us your goal — what will you use the capital for?"); return; }
    if (fundingPref !== "business") {
      const inc = Number(annualIncome.replace(/[^0-9.]/g, ""));
      if (!Number.isFinite(inc) || inc <= 0) { setError("Enter your yearly income before taxes — lenders decide personal funding on income, not just your score."); return; }
      if (!incomeSource) { setError("Where does your income come from?"); return; }
    }
    if (fundingPref !== "personal") {
      if (!bizDescription.trim()) { setError("Tell us what your business is / what it does."); return; }
      if (!bizState) { setError("Select the state your business is located in — some lenders only fund certain states."); return; }
    }
    if (otherIncome.some((o) => (o.source.trim() && !o.amount.trim()) || (!o.source.trim() && o.amount.trim()))) {
      setError("Each extra income needs both a source and an amount — or remove the row."); return;
    }
    setBusy(true); setError(null);
    try {
      await intake.saveFunding(token, {
        capital_requested: cap,
        use_of_funds: useOfFunds.trim(),
        funding_preference: fundingPref,
        annual_income: annualIncome ? Number(annualIncome.replace(/[^0-9.]/g, "")) : undefined,
        income_source: incomeSource || undefined,
        employer_name: employerName.trim() || undefined,
        employer_address: employerAddress.trim() || undefined,
        employer_phone: employerPhone.trim() || undefined,
        employment_years: empYears.trim() ? toNum(empYears) : undefined,
        employment_months: empMonths.trim() ? toNum(empMonths) : undefined,
        other_income: otherIncome
          .filter((o) => o.source.trim() && o.amount.trim())
          .map((o) => ({ source: o.source.trim(), amount: toNum(o.amount), frequency: o.frequency })),
        checking_balance: checkingBal.trim() ? toNum(checkingBal) : undefined,
        savings_balance: savingsBal.trim() ? toNum(savingsBal) : undefined,
        housing_status: housingStatus || undefined,
        housing_payment: housingPayment ? Number(housingPayment.replace(/[^0-9.]/g, "")) : undefined,
        business_description: bizDescription.trim(),
        business_state: bizState,
        business_name: bizName.trim() || undefined,
        business_entity_type: bizEntity || undefined,
        business_ein: bizEin.trim() || undefined,
        business_industry: bizIndustry.trim() || undefined,
        business_city: bizCity.trim() || undefined,
        business_zip: bizZip.trim() || undefined,
        business_start_date: bizStart || undefined,
        annual_revenue: annualRev ? Number(annualRev.replace(/[^0-9.]/g, "")) : undefined,
      });
      setStep("documents");
    } catch (e: any) { setError(e?.message || "Save failed"); }
    finally { setBusy(false); }
  }

  async function uploadOne(kind: DocKind, file: File | null): Promise<"done" | "error"> {
    if (!file) return docStatus[kind] === "done" ? "done" : "error";
    setDocStatus(s => ({ ...s, [kind]: "uploading" }));
    try {
      await intake.uploadDocument(token, kind, file);
      setDocStatus(s => ({ ...s, [kind]: "done" }));
      return "done";
    } catch (e: any) {
      setDocStatus(s => ({ ...s, [kind]: "error" }));
      setError(e?.message || "Upload failed — please try that file again.");
      return "error";
    }
  }

  // v1.1 gating: every REQUIRED document must be uploaded (status done)
  // before Continue; missing ones name themselves in the error.
  async function saveDocuments() {
    setBusy(true); setError(null);
    try {
      // Build results locally as we go — reading docStatus here would be the
      // STALE render-time closure and could report a just-uploaded doc as
      // missing (the pre-fix bug: press Continue right after selecting a file).
      const results: Record<string, string> = { ...docStatus };
      for (const d of DOCS) {
        const f = docFiles[d.key];
        if (f && results[d.key] !== "done") results[d.key] = await uploadOne(d.key, f);
      }
      const missing = DOCS.filter(d => d.required && results[d.key] !== "done").map(d => d.label);
      const failed = DOCS.filter(d => results[d.key] === "error").map(d => d.label);
      if (failed.length) {
        setError(`Upload failed for: ${failed.join(", ")}. Please choose the file again to retry.`);
        return;
      }
      if (missing.length) {
        setError(`Still needed: ${missing.join(", ")}.`);
        return;
      }
      setStep("monitoring");
    } finally { setBusy(false); }
  }

  async function saveMonitoring() {
    if (!monUser.trim() || !monPass) {
      const manual = monProvider === "experian" || monProvider === "other";
      setError(
        manual
          ? "Enter your credit-monitoring login so your advisor can collect your report."
          : hasMonitoring === "no"
            ? `After you create your ${MONITORING_PROVIDERS.find((p) => p.value === monProvider)?.label ?? "monitoring"} account, enter that login here so we can pull your report.`
            : "Enter your credit-monitoring username and password so we can pull your report.");
      return;
    }
    if (!noCreditKarma && (!ckEmail.trim() || !ckPass)) {
      setError("Enter your Credit Karma login — or check the box if you don't have a Credit Karma account.");
      return;
    }
    setBusy(true); setError(null);
    try {
      await intake.saveMonitoring(token, {
        provider: monProvider,
        username: monUser.trim(),
        password: monPass,
        credit_karma: !noCreditKarma && ckEmail.trim()
          ? { email: ckEmail.trim(), password: ckPass }
          : undefined,
      });
      setStep("review");
    } catch (e: any) { setError(e?.message || "Save failed"); }
    finally { setBusy(false); }
  }

  async function submit() {
    setBusy(true); setError(null);
    try {
      intake.setCreditAuthorization(token, authName.trim());
      await intake.submit(token);
      setSubmitted(true);
    } catch (e: any) { setError(e?.message || "Submit failed"); }
    finally { setBusy(false); }
  }

  if (loadError) {
    return (
      <div className="intake-page has-signature-bg">
      <SignatureBg />
        <div className="intake-card">
          <h1>Link not available</h1>
          <p className="muted">{loadError}</p>
          <p className="muted small">If you believe this is a mistake, reply to the message we sent and we'll send a new link.</p>
        </div>
      </div>
    );
  }

  if (!ctx) {
    return <div className="intake-page"><div className="intake-card"><p className="muted">Loading…</p></div></div>;
  }

  if (submitted) {
    return (
      <div className="intake-page">
        <div className="intake-card thank-you">
          <CheckCircle2 size={48} color="var(--ne-red)" aria-hidden="true" />
          <h1>Thank you, {ctx.client.legal_name.split(" ")[0]}.</h1>
          <p>Your funding application is in. Here's what happens next:</p>
          <ol className="next-steps">
            <li>We {monProvider === "experian" || monProvider === "other" ? "collect your credit report" : `pull your credit report from ${monProvider === "smart_credit" ? "SmartCredit" : "MyFreeScoreNow"}`}.</li>
            <li>We run your full funding qualification — both the personal and business paths.</li>
            <li>You get your results and, if you qualify, your funding agreement and matched lenders.</li>
          </ol>
          <p className="muted small">
            You'll hear from us at every milestone. No further action is needed from you right now.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="intake-page">
      <div className="intake-card">
        <div className="intake-header">
          <div className="brand-mark">
            <img src="/brand-logo.png" alt="" />
          </div>
          <div>
            <strong>Alief Funding Hub</strong>
            <span>Secure funding intake for {ctx.client.legal_name}</span>
          </div>
        </div>

        <div className="step-progress" role="list" aria-label="Intake progress">
          {STEP_ORDER.map((s, i) => (
            <div
              key={s}
              role="listitem"
              aria-current={i === stepIndex ? "step" : undefined}
              aria-label={`Step ${i + 1} of ${STEP_ORDER.length}: ${s}${i < stepIndex ? " (complete)" : i === stepIndex ? " (current)" : ""}`}
              className={`step-dot ${i <= stepIndex ? "done" : ""} ${i === stepIndex ? "current" : ""}`}
            >
              {i + 1}
            </div>
          ))}
        </div>

        {step === "identity" && (
          <>
            <h1>Identity</h1>
            {resumed && (
              <div className="panel" style={{ padding: "12px 16px", marginBottom: 14 }}>
                <strong>Welcome back — your progress was saved.</strong>
                <p className="muted small" style={{ margin: "4px 0 0" }}>
                  For your security we never store your SSN or passwords between visits, so
                  you'll re-enter those. Everything else is already filled in.
                </p>
              </div>
            )}
            <p className="muted small">We use this to verify the credit report we pull matches you, and only you.</p>
            <label className="form-field">
              <span>Legal name (as it appears on your ID)</span>
              <input value={legalName} onChange={(e) => setLegalName(e.target.value)} required />
            </label>
            <label className="form-field">
              <span>Date of birth</span>
              <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} required />
            </label>
            <label className="form-field">
              <span>Social Security Number</span>
              <input
                type="password"
                inputMode="numeric"
                pattern="\d{3}-?\d{2}-?\d{4}"
                placeholder="XXX-XX-XXXX"
                value={ssn}
                onChange={(e) => setSsn(e.target.value)}
                required
              />
              <span className="muted small">Encrypted at rest. Used only to verify your credit profile.</span>
            </label>
            <label className="form-field">
              <span>Street address</span>
              <input value={addr1} onChange={(e) => setAddr1(e.target.value)} required />
            </label>
            <div className="form-row">
              <label className="form-field">
                <span>City</span>
                <input value={city} onChange={(e) => setCity(e.target.value)} required />
              </label>
              <label className="form-field">
                <span>State</span>
                <input value={state} onChange={(e) => setState(e.target.value)} maxLength={2} required />
              </label>
              <label className="form-field">
                <span>ZIP</span>
                <input value={zip} onChange={(e) => setZip(e.target.value)} required />
              </label>
            </div>
            <label className="form-field">
              <span>Phone number</span>
              <input inputMode="tel" placeholder="(803) 555-0142" value={phone}
                onChange={(e) => setPhone(e.target.value)} />
              <span className="muted small">Every lender application asks for this.</span>
            </label>
            {error && <p className="error-text">{error}</p>}
            <button className="primary-button intake-next" onClick={saveIdentity} disabled={busy}>
              {busy ? "Saving…" : "Continue"} <ChevronRight size={16} aria-hidden="true" />
            </button>
          </>
        )}

        {step === "business" && (
          <>
            <h1>Your funding goal</h1>
            <p className="muted small">
              This is what we match against our lender network — the more complete, the better your matches.
            </p>

            <p style={{ fontWeight: 700, margin: "18px 0 6px" }}>What kind of funding are you after?</p>
            <div className="intake-choice-row">
              {([
                ["personal", "Personal only"],
                ["business", "Business only"],
                ["both", "Both"],
              ] as const).map(([val, label]) => (
                <button key={val} type="button"
                  className={fundingPref === val ? "primary-button" : "ghost-button"}
                  onClick={() => setFundingPref(val)}>
                  {label}
                </button>
              ))}
            </div>
            <p className="muted small" style={{ marginTop: 6 }}>
              {fundingPref === "personal"
                ? "Personal funding only — you can skip the business questions below."
                : "We'll need a few details about the business so we can match the right lenders."}
            </p>
            <label className="form-field">
              <span>How much capital are you looking to get?</span>
              <input inputMode="numeric" placeholder="$50,000" value={capital} onChange={(e) => setCapital(e.target.value)} required />
            </label>
            <label className="form-field">
              <span>What is your goal — what will you use it for?</span>
              <textarea rows={3} placeholder="Working capital, new equipment, hiring, expansion…" value={useOfFunds} onChange={(e) => setUseOfFunds(e.target.value)} required />
            </label>
            <p style={{ fontWeight: 700, margin: "22px 0 4px" }}>Employment &amp; income</p>
            <p className="muted small" style={{ marginBottom: 10 }}>
              {fundingPref !== "business"
                ? "Lenders decide personal funding on income and stability — not only your credit score. This is the part that gets you approved."
                : "Most lenders also ask about the owner's employment and income — filling this in now saves you a phone call later."}
            </p>
            <div className="form-row">
              <label className="form-field">
                <span>Employer / business name</span>
                <input value={employerName} onChange={(e) => setEmployerName(e.target.value)} />
              </label>
              <label className="form-field">
                <span>Employer phone number</span>
                <input inputMode="tel" placeholder="(555) 555-5555" value={employerPhone} onChange={(e) => setEmployerPhone(e.target.value)} />
              </label>
            </div>
            <label className="form-field">
              <span>Employer address</span>
              <input placeholder="Street, city, state, ZIP" value={employerAddress} onChange={(e) => setEmployerAddress(e.target.value)} />
            </label>
            <div className="form-row">
              <label className="form-field">
                <span>How long have you worked there? (years)</span>
                <input inputMode="numeric" placeholder="3" value={empYears} onChange={(e) => setEmpYears(e.target.value)} />
              </label>
              <label className="form-field">
                <span>…and months</span>
                <select value={empMonths} onChange={(e) => setEmpMonths(e.target.value)}>
                  <option value="">0</option>
                  {Array.from({ length: 11 }, (_, i) => i + 1).map((m) => <option key={m} value={String(m)}>{m}</option>)}
                </select>
              </label>
            </div>
            <div className="form-row">
              <label className="form-field">
                <span>Annual salary / yearly income before taxes{fundingPref !== "business" ? "" : " (optional)"}</span>
                <input inputMode="numeric" placeholder="$48,000" value={annualIncome}
                  onChange={(e) => setAnnualIncome(e.target.value)} />
              </label>
              <label className="form-field">
                <span>Where does it come from?</span>
                <select value={incomeSource} onChange={(e) => setIncomeSource(e.target.value)}>
                  <option value="">Select…</option>
                  <option value="employed">A job (W-2)</option>
                  <option value="self_employed">Self-employed / 1099</option>
                  <option value="retired">Retirement / pension</option>
                  <option value="benefits">Disability / Social Security / benefits</option>
                  <option value="other">Other</option>
                </select>
              </label>
            </div>

            <p style={{ fontWeight: 700, margin: "18px 0 4px" }}>Other income (optional)</p>
            <p className="muted small" style={{ marginBottom: 8 }}>Side business, rental property, child support, a second job — add as many as you have.</p>
            {otherIncome.map((o, idx) => (
              <div className="form-row" key={idx} style={{ alignItems: "end" }}>
                <label className="form-field">
                  <span>Source</span>
                  <input placeholder="Rental property" value={o.source}
                    onChange={(e) => setOtherIncome((rows) => rows.map((r, k) => k === idx ? { ...r, source: e.target.value } : r))} />
                </label>
                <label className="form-field">
                  <span>Amount</span>
                  <input inputMode="numeric" placeholder="$800" value={o.amount}
                    onChange={(e) => setOtherIncome((rows) => rows.map((r, k) => k === idx ? { ...r, amount: e.target.value } : r))} />
                </label>
                <label className="form-field">
                  <span>Per</span>
                  <select value={o.frequency}
                    onChange={(e) => setOtherIncome((rows) => rows.map((r, k) => k === idx ? { ...r, frequency: e.target.value as "monthly" | "annual" } : r))}>
                    <option value="monthly">Month</option>
                    <option value="annual">Year</option>
                  </select>
                </label>
                <button type="button" className="ghost-button" aria-label={`Remove income ${idx + 1}`} style={{ marginBottom: 12 }}
                  onClick={() => setOtherIncome((rows) => rows.filter((_, k) => k !== idx))}>Remove</button>
              </div>
            ))}
            {otherIncome.length < 10 && (
              <button type="button" className="ghost-button" style={{ marginBottom: 14 }}
                onClick={() => setOtherIncome((rows) => [...rows, { source: "", amount: "", frequency: "monthly" }])}>
                + Add other income
              </button>
            )}

            <p style={{ fontWeight: 700, margin: "18px 0 4px" }}>Housing &amp; assets</p>
            <div className="form-row">
              <label className="form-field">
                <span>Do you rent or own?</span>
                <select value={housingStatus} onChange={(e) => setHousingStatus(e.target.value)}>
                  <option value="">Select…</option>
                  <option value="rent">Rent</option>
                  <option value="own_mortgage">Own with a mortgage</option>
                  <option value="own_outright">Own outright</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <label className="form-field">
                <span>Total monthly housing cost (rent or mortgage)</span>
                <input inputMode="numeric" placeholder="$1,200" value={housingPayment}
                  onChange={(e) => setHousingPayment(e.target.value)} />
              </label>
            </div>
            <div className="form-row">
              <label className="form-field">
                <span>Checking account balance</span>
                <input inputMode="numeric" placeholder="$2,500" value={checkingBal} onChange={(e) => setCheckingBal(e.target.value)} />
              </label>
              <label className="form-field">
                <span>Savings account balance</span>
                <input inputMode="numeric" placeholder="$5,000" value={savingsBal} onChange={(e) => setSavingsBal(e.target.value)} />
              </label>
            </div>

            {fundingPref !== "personal" && (<>
            <label className="form-field">
              <span>What is your business? What does it do?</span>
              <textarea rows={3} placeholder="e.g. Home healthcare agency serving New Orleans — 12 caregivers, billing Medicaid and private insurance." value={bizDescription} onChange={(e) => setBizDescription(e.target.value)} required />
            </label>
            <div className="form-row">
              <label className="form-field">
                <span>Business name</span>
                <input value={bizName} onChange={(e) => setBizName(e.target.value)} />
              </label>
              <label className="form-field">
                <span>Entity type</span>
                <select value={bizEntity} onChange={(e) => setBizEntity(e.target.value)}>
                  <option value="">Select…</option>
                  <option>LLC</option>
                  <option>Corporation</option>
                  <option>S-Corp</option>
                  <option>Partnership</option>
                  <option>Sole Proprietor</option>
                  <option>Nonprofit</option>
                </select>
              </label>
            </div>
            <div className="form-row">
              <label className="form-field">
                <span>EIN (if you have one)</span>
                <input placeholder="XX-XXXXXXX" value={bizEin} onChange={(e) => setBizEin(e.target.value)} />
              </label>
              <label className="form-field">
                <span>Industry</span>
                <input placeholder="Healthcare, trucking, retail…" value={bizIndustry} onChange={(e) => setBizIndustry(e.target.value)} />
              </label>
            </div>
            <div className="form-row">
              <label className="form-field">
                <span>Business city</span>
                <input value={bizCity} onChange={(e) => setBizCity(e.target.value)} />
              </label>
              <label className="form-field">
                <span>Business state</span>
                <select value={bizState} onChange={(e) => setBizState(e.target.value)} required>
                  <option value="">Select…</option>
                  {US_STATES.map((st) => <option key={st} value={st}>{st}</option>)}
                </select>
              </label>
              <label className="form-field">
                <span>ZIP</span>
                <input value={bizZip} onChange={(e) => setBizZip(e.target.value)} />
              </label>
            </div>
            <p className="muted small" style={{ marginTop: -4 }}>
              Location matters — some lenders only fund businesses in certain states.
            </p>
            <div className="form-row">
              <label className="form-field">
                <span>When did the business start?</span>
                <input type="date" value={bizStart} onChange={(e) => setBizStart(e.target.value)} />
              </label>
              <label className="form-field">
                <span>Annual revenue (approx.)</span>
                <input inputMode="numeric" placeholder="$250,000" value={annualRev} onChange={(e) => setAnnualRev(e.target.value)} />
              </label>
            </div>
            </>)}
            {error && <p className="error-text">{error}</p>}
            <button className="ghost-button" style={{ marginRight: 10 }} onClick={() => setStep("identity")}>Back</button>
            <button className="primary-button intake-next" onClick={saveBusiness} disabled={busy}>
              {busy ? "Saving…" : "Continue"} <ChevronRight size={16} aria-hidden="true" />
            </button>
          </>
        )}

        {step === "documents" && (
          <>
            <h1>Documents</h1>
            <p className="muted small">
              Your ID verifies who you are. The Secretary of State and EIN documents are optional,
              but having them on file speeds up every lender application.
            </p>

            {DOCS.map((d) => (
              <div key={d.key} className="doc-upload">
                <div>
                  <strong>{d.label}{d.required ? "" : " (optional)"}</strong>
                  <span className="muted small">{d.hint} Max 10 MB.</span>
                </div>
                <div className="doc-upload-actions">
                  <label className="ghost-button">
                    <Upload size={14} aria-hidden="true" />
                    {docFiles[d.key] ? docFiles[d.key]!.name : "Choose file"}
                    <input
                      type="file"
                      hidden
                      accept={d.accept}
                      onChange={(e) => {
                        const f = e.target.files?.[0] || null;
                        setDocFiles(prev => ({ ...prev, [d.key]: f || undefined }));
                        if (f) uploadOne(d.key, f);
                      }}
                    />
                  </label>
                  {docStatus[d.key] === "uploading" && <span className="muted small">Uploading…</span>}
                  {docStatus[d.key] === "done" && <CheckCircle2 size={16} color="var(--ne-success)" aria-label="Uploaded" />}
                  {docStatus[d.key] === "error" && <span className="error-text">Failed — choose the file again</span>}
                </div>
              </div>
            ))}

            {error && <p className="error-text">{error}</p>}
            <button className="ghost-button" style={{ marginRight: 10 }} onClick={() => setStep("business")}>Back</button>
            <button className="primary-button intake-next" onClick={saveDocuments}
              disabled={busy || Object.values(docStatus).includes("uploading")}>
              {busy ? "Saving…" : Object.values(docStatus).includes("uploading") ? "Uploading…" : "Continue"} <ChevronRight size={16} aria-hidden="true" />
            </button>
          </>
        )}

        {step === "monitoring" && (
          <>
            <h1>Credit access</h1>
            <p className="muted small">
              We pull your credit report through your monitoring account and use your Credit Karma
              for pre-qualified offers. Logins are encrypted and only used for your file — never shared.
            </p>

            {/* ── Do they have MyFreeScoreNow yet? ── */}
            <div className="intake-choice-row">
              <button type="button"
                className={hasMonitoring === "yes" ? "primary-button" : "ghost-button"}
                onClick={() => setHasMonitoring("yes")}>
                I have {MONITORING_PROVIDERS.find((p) => p.value === monProvider)?.label ?? "monitoring"}
              </button>
              <button type="button"
                className={hasMonitoring === "no" ? "primary-button" : "ghost-button"}
                onClick={() => setHasMonitoring("no")}>
                I don't have it yet
              </button>
            </div>

            {hasMonitoring === "no" && ctx?.mfsn_affiliate_url && monProvider !== "experian" && monProvider !== "other" && (
              <div className="panel intake-signup-panel">
                <strong>Step 1 — create your MyFreeScoreNow account</strong>
                <ol className="muted small" style={{ margin: "8px 0 12px", paddingLeft: 18, lineHeight: 1.7 }}>
                  <li>Tap the button below — signup opens in a new tab and takes about 2 minutes.</li>
                  <li>Keep this page open. Come back when you're done.</li>
                  <li>Enter your new login in Step 2 below.</li>
                </ol>
                <a className="primary-button" style={{ display: "inline-flex" }}
                   href={ctx.mfsn_affiliate_url} target="_blank" rel="noopener noreferrer">
                  Create my MyFreeScoreNow account →
                </a>
              </div>
            )}

            {hasMonitoring !== null && (
              <>
                <p style={{ fontWeight: 700, margin: "18px 0 4px" }}>
                  {hasMonitoring === "no" && monProvider !== "experian" && monProvider !== "other" ? "Step 2 — enter your new login" : "Your monitoring login"}
                </p>
                <label className="form-field">
                  <span>Provider</span>
                  <select value={monProvider} onChange={(e) => setMonProvider(e.target.value as any)}>
                    {MONITORING_PROVIDERS.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </label>
                <label className="form-field">
                  <span>{monProvider === "my_free_score_now" ? "Email (your MyFreeScoreNow login)" : `Username (your ${MONITORING_PROVIDERS.find((p) => p.value === monProvider)?.label ?? ""} login)`}</span>
                  <input value={monUser} onChange={(e) => setMonUser(e.target.value)} autoComplete="off" />
                </label>
                <label className="form-field">
                  <span>Password</span>
                  <input type="password" value={monPass} onChange={(e) => setMonPass(e.target.value)} autoComplete="off" />
                </label>

                {/* ── Credit Karma ── */}
                <p style={{ fontWeight: 700, margin: "22px 0 4px" }}>Your Credit Karma login</p>
                <p className="muted small" style={{ marginBottom: 10 }}>
                  Credit Karma shows us the offers you're already pre-qualified for — it's free for
                  you and helps us find approvals faster.
                </p>
                {!noCreditKarma && (
                  <>
                    <label className="form-field">
                      <span>Credit Karma email</span>
                      <input value={ckEmail} onChange={(e) => setCkEmail(e.target.value)} autoComplete="off" inputMode="email" />
                    </label>
                    <label className="form-field">
                      <span>Credit Karma password</span>
                      <input type="password" value={ckPass} onChange={(e) => setCkPass(e.target.value)} autoComplete="off" />
                    </label>
                  </>
                )}
                <label className="muted small" style={{ display: "flex", gap: 8, alignItems: "center", margin: "8px 0 0", cursor: "pointer" }}>
                  <input type="checkbox" checked={noCreditKarma} onChange={(e) => setNoCreditKarma(e.target.checked)} />
                  I don't have a Credit Karma account
                </label>

                {error && <p className="error-text">{error}</p>}
                <button className="ghost-button" style={{ marginRight: 10 }} onClick={() => setStep("documents")}>Back</button>
            <button className="primary-button intake-next" onClick={saveMonitoring} disabled={busy}>
                  {busy ? "Saving…" : "Continue"} <ChevronRight size={16} aria-hidden="true" />
                </button>
              </>
            )}
            {hasMonitoring === null && error && <p className="error-text">{error}</p>}
          </>
        )}

        {step === "review" && (
          <>
            <h1>Review and submit</h1>
            {(() => {
              const missing: Array<{ label: string; step: Step }> = [];
              if (!ssn.trim()) missing.push({ label: "Your Social Security number", step: "identity" });
              if (hasMonitoring === "yes" && !monPass) missing.push({ label: "Your credit monitoring password", step: "monitoring" });
              if (!noCreditKarma && ckEmail.trim() && !ckPass) missing.push({ label: "Your Credit Karma password", step: "monitoring" });
              if (!missing.length) {
                return <p className="muted small">Everything looks good. Submit to send your application to our team.</p>;
              }
              return (
                <div className="panel" style={{ padding: "14px 16px", marginBottom: 14, borderLeft: "3px solid var(--ne-red-bright)" }}>
                  <strong>Two seconds — we still need {missing.length === 1 ? "one thing" : `${missing.length} things`}.</strong>
                  <p className="muted small" style={{ margin: "4px 0 10px" }}>
                    For your security we never save these between visits, so they have to be typed
                    again. Everything else is already saved.
                  </p>
                  {missing.map((m) => (
                    <button key={m.label} className="ghost-button" style={{ display: "block", marginBottom: 6 }}
                      onClick={() => setStep(m.step)}>
                      Add {m.label} →
                    </button>
                  ))}
                </div>
              );
            })()}
            <ul className="review-list">
              <li><CheckCircle2 size={16} color="var(--ne-success)" aria-hidden="true" /> Identity captured</li>
              <li><CheckCircle2 size={16} color="var(--ne-success)" aria-hidden="true" /> Business &amp; funding goal captured</li>
              <li><CheckCircle2 size={16} color="var(--ne-success)" aria-hidden="true" /> Documents uploaded</li>
              <li><CheckCircle2 size={16} color="var(--ne-success)" aria-hidden="true" /> Credit monitoring login saved</li>
              {!noCreditKarma && <li><CheckCircle2 size={16} color="var(--ne-success)" aria-hidden="true" /> Credit Karma login saved</li>}
            </ul>
            {ctx.credit_authorization && (
              <div className="panel" style={{ padding: "16px 18px", margin: "18px 0 4px" }}>
                <strong style={{ display: "block", marginBottom: 4 }}>{ctx.credit_authorization.title}</strong>
                <p className="muted small" style={{ margin: "0 0 10px" }}>{ctx.credit_authorization.intro}</p>
                <div style={{ maxHeight: 200, overflowY: "auto", border: "1px solid var(--ne-line)", borderRadius: 8, padding: "12px 14px", background: "var(--ne-black-2)" }}>
                  <ol style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 9 }}>
                    {ctx.credit_authorization.points.map((pt, i) => (
                      <li key={i} className="muted small" style={{ lineHeight: 1.55 }}>{pt}</li>
                    ))}
                  </ol>
                </div>
                <p className="muted small" style={{ margin: "10px 0 0" }}>{ctx.credit_authorization.signature_prompt}</p>
                <label style={{ display: "flex", gap: 10, alignItems: "flex-start", margin: "12px 0", cursor: "pointer" }}>
                  <input type="checkbox" checked={authAgree} onChange={(e) => setAuthAgree(e.target.checked)}
                    style={{ marginTop: 3, width: 18, height: 18, accentColor: "var(--ne-red-bright)" }} />
                  <span className="small">
                    I have read the authorization above and I authorize you to obtain my credit report and score.
                  </span>
                </label>
                <label className="form-field">
                  <span>Sign — type your full legal name</span>
                  <input value={authName} onChange={(e) => setAuthName(e.target.value)} autoComplete="name"
                    placeholder={legalName || "First and last name"} />
                </label>
                <p className="muted small" style={{ margin: "8px 0 0" }}>{ctx.credit_authorization.footer}</p>
              </div>
            )}
            {error && <p className="error-text">{error}</p>}
            <button className="ghost-button" style={{ marginRight: 10 }} onClick={() => setStep("monitoring")}>Back</button>
            <button className="primary-button intake-next" onClick={submit}
              disabled={busy || (!!ctx.credit_authorization && (!authAgree || authName.trim().length < 2))}>
              {busy ? "Submitting…" : "Submit application"} <ChevronRight size={16} aria-hidden="true" />
            </button>
            {ctx.credit_authorization && (!authAgree || authName.trim().length < 2) && (
              <p className="muted small" style={{ marginTop: 10 }}>
                Tick the authorization box and sign your full legal name to submit.
              </p>
            )}
          </>
        )}
      </div>

      <p className="intake-footer">
        Secure funding intake · This link is single-use and expires {new Date(ctx.invitation.expires_at).toLocaleDateString()}.
      </p>
    </div>
  );
}
